import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { playAgain, endGame, deleteRoom } from "./scattergoriesRoom"
import { Screen, SgButton, PlayerDot, C, F } from "./ScattergoriesUI"
import { playSound } from "../../../utils/ballparkSounds"

const GameOverScreen = ({ room, code, playerId, isHost }) => {
  const navigate = useNavigate()
  const players = Object.entries(room.players)
  const sorted = [...players].sort((a, b) => b[1].score - a[1].score)
  
  const hostName = room.players[room.hostId]?.name || "Host"

  const handleClose = async () => {
    playSound("click")
    try {
      await deleteRoom(code)
    } catch (e) {
      console.error(e)
    }
    navigate("/scattergories")
  }

  useEffect(() => {
    playSound("gameover")
  }, [])

  const handleRematch = async () => {
    playSound("click")
    await playAgain(code, hostName)
  }

  // Determine top three for podium
  const first = sorted[0]
  const second = sorted[1]
  const third = sorted[2]

  return (
    <Screen style={{ gap: 20, maxHeight: "100dvh", overflow: "hidden", padding: "16px 20px 20px" }}>
      <div style={{ textAlign: "center", marginBottom: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 32, display: "block" }}>🏆</span>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 36, color: C.indigo, marginTop: 8 }}>
          Game Over!
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.muted, marginTop: 4 }}>
          Here are the final standings
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 20, paddingRight: 4, margin: "4px 0" }}>
        {/* Podium Visualization */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            gap: 16,
            height: 195,
            margin: "16px 0 16px",
            background: "linear-gradient(to top, rgba(79, 70, 229, 0.03) 0%, transparent 100%)",
            borderRadius: 20,
            paddingBottom: 8,
            flexShrink: 0,
          }}
        >
          {/* 2nd Place */}
          {second && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 90 }}>
              <PlayerDot color={second[1].color} size={36} />
              <div style={{ fontWeight: 800, fontSize: 13, color: C.ink, marginTop: 6, textAlign: "center", textOverflow: "ellipsis", overflow: "hidden", width: "100%", whiteSpace: "nowrap" }}>
                {second[1].name}
              </div>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.muted }}>
                {second[1].score} pts
              </div>
              <div
                style={{
                  width: "100%",
                  height: 50,
                  background: C.border,
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: F.display,
                  fontWeight: 900,
                  fontSize: 20,
                  color: C.muted,
                  marginTop: 6,
                }}
              >
                2
              </div>
            </div>
          )}

          {/* 1st Place */}
          {first && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 100 }}>
              <span style={{ fontSize: 20, marginBottom: -6 }}>👑</span>
              <PlayerDot color={first[1].color} size={48} />
              <div style={{ fontWeight: 900, fontSize: 14, color: C.indigo, marginTop: 6, textAlign: "center", textOverflow: "ellipsis", overflow: "hidden", width: "100%", whiteSpace: "nowrap" }}>
                {first[1].name}
              </div>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.indigo }}>
                {first[1].score} pts
              </div>
              <div
                style={{
                  width: "100%",
                  height: 75,
                  background: C.indigo,
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: F.display,
                  fontWeight: 900,
                  fontSize: 24,
                  color: C.white,
                  marginTop: 6,
                  boxShadow: "0 4px 10px rgba(79, 70, 229, 0.2)",
                }}
              >
                1
              </div>
            </div>
          )}

          {/* 3rd Place */}
          {third && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 90 }}>
              <PlayerDot color={third[1].color} size={32} />
              <div style={{ fontWeight: 800, fontSize: 13, color: C.ink, marginTop: 6, textAlign: "center", textOverflow: "ellipsis", overflow: "hidden", width: "100%", whiteSpace: "nowrap" }}>
                {third[1].name}
              </div>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.muted }}>
                {third[1].score} pts
              </div>
              <div
                style={{
                  width: "100%",
                  height: 38,
                  background: C.borderDark,
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: F.display,
                  fontWeight: 900,
                  fontSize: 18,
                  color: C.muted,
                  marginTop: 6,
                }}
              >
                3
              </div>
            </div>
          )}
        </div>

        {/* Full Scoreboard */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, letterSpacing: 1.5, fontFamily: F.display }}>
            SCOREBOARD
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {sorted.map(([pid, p], idx) => (
              <div
                key={pid}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  background: C.card,
                  border: `1.5px solid ${idx === 0 ? C.indigo : C.border}`,
                  borderRadius: 14,
                  padding: "10px 14px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.01)",
                }}
              >
                <span style={{ fontWeight: 900, fontSize: 14, color: idx === 0 ? C.indigo : C.muted, width: 16 }}>
                  {idx + 1}
                </span>
                <PlayerDot color={p.color} size={22} />
                <span style={{ fontWeight: 800, fontSize: 15, color: C.ink }}>
                  {p.name} {pid === playerId && "(You)"}
                </span>
                <span
                  style={{
                    marginLeft: "auto",
                    fontFamily: F.display,
                    fontWeight: 900,
                    fontSize: 18,
                    color: idx === 0 ? C.indigo : C.coral,
                  }}
                >
                  {p.score} pts
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rematch Controls */}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}>
        {isHost ? (
          <>
            <SgButton onClick={handleRematch}>
              Play Again (Rematch)
            </SgButton>
            <SgButton variant="ghost" onClick={handleClose} style={{ fontSize: 13 }}>
              Close Room
            </SgButton>
          </>
        ) : (
          <div
            style={{
              background: C.panel,
              borderRadius: 14,
              padding: "14px",
              textAlign: "center",
              fontWeight: 800,
              fontSize: 14,
              color: C.muted,
              fontFamily: F.display,
            }}
          >
            Waiting for host to start a rematch…
          </div>
        )}
      </div>
    </Screen>
  )
}

export default GameOverScreen
